// import node module libraries
import React, { Fragment, useEffect, useState } from 'react';
import { Alert, Breadcrumb, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';

// import custom components
import GKStepper from 'components/elements/stepper/GKStepper';

// import sub components ( Steps )
import InformationsPersonnelles from './steps/InformationsPersonnelles';
import InformationsMedicales from './steps/InformationsMedicales';
import InformationsScolaires from './steps/InformationsScolaires';

// import services
import EleveService from '../../services/EleveService';
import AnneescolaireService from '../../services/AnneescolaireService';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';

const AddNewStudent = () => {
	const [personalInfo, setPersonalInfo] = useState({});
	const [medicalInfo, setMedicalInfo] = useState({});
	const [schoolInfo, setSchoolInfo] = useState({});
	const navigate = useNavigate();

	const handlePersonalInfoChange = (data) => {
		setPersonalInfo(data);
	};

	const handleMedicalInfoChange = (data) => {
		setMedicalInfo(data);
	};

	const handleSchoolInfoChange = (data) => {
		setSchoolInfo(data);
	};

	const [currentStep, setCurrentStep] = useState(1);
	const next = () => {
		setCurrentStep(currentStep === 3 ? 1 : currentStep + 1);
	};
	const previous = () => {
		setCurrentStep(currentStep === 1 ? 1 : currentStep - 1);
	};

	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [currentSchoolYear, setCurrentSchoolYear] = useState(null);
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');

	useEffect(() => {
		const fetchCurrentSchoolYear = async () => {
			try {
				const schoolYear = await AnneescolaireService.getCurrentAnneescolaire();
				setCurrentSchoolYear(schoolYear.data);
			} catch (error) {
				console.error(
					"Erreur lors de la récupération de l'année scolaire en cours:",
					error
				);
				alert(
					`Erreur lors de la récupération de l'année scolaire en cours. Détails de l'erreur : ${error.message}`
				);
			}
		};

		fetchCurrentSchoolYear();
	}, []);

	const steps = [
		{
			id: 1,
			title: 'Informations personnelles',
			content: (
				<InformationsPersonnelles
					next={next}
					onChange={handlePersonalInfoChange}
					initialValues={personalInfo}
				/>
			)
		},
		{
			id: 2,
			title: 'Informations médicales',
			content: (
				<InformationsMedicales
					next={next}
					previous={previous}
					onChange={handleMedicalInfoChange}
					initialValues={medicalInfo}
				/>
			)
		},
		{
			id: 3,
			title: 'Informations scolaires',
			content: (
				<InformationsScolaires
					previous={previous}
					onChange={handleSchoolInfoChange}
					initialValues={schoolInfo}
				/>
			)
		}
	];

	const safeValue = (value) => (value !== undefined ? value : '');

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const studentData = new FormData();

				if (currentSchoolYear === null) {
					alert(
						"Erreur lors de la récupération de l'année scolaire en cours. Veuillez réessayer plus tard."
					);
					return;
				}

				// Add all your form data to the FormData
				studentData.append('nom', safeValue(personalInfo.nom));
				studentData.append('prenom', safeValue(personalInfo.prenom));
				studentData.append(
					'date_naissance',
					safeValue(personalInfo.date_naissance)
				);
				studentData.append(
					'lieu_naissance',
					safeValue(personalInfo.lieu_naissance)
				);
				studentData.append('genre', safeValue(personalInfo.genre));
				studentData.append(
					'langue_maternelle',
					safeValue(personalInfo.langue_maternelle)
				);
				studentData.append('statut_eleve', safeValue(schoolInfo.statut_eleve));
				studentData.append(
					'ancienne_ecole',
					safeValue(schoolInfo.ancienne_ecole)
				);
				// Un élève venant d'un autre établissement doit fournir le nom de son ancienne école et son bulletin de l'année précédente.

				if (schoolInfo.is_new_student) {
					studentData.append(
						'classe_redoublement',
						schoolInfo.classe_redoublement ? schoolInfo.classe_redoublement : ''
					);
				} else {
					studentData.append(
						'classe_redoublement_new',
						schoolInfo.classe_redoublement ? schoolInfo.classe_redoublement : ''
					);
				}

				studentData.append(
					'maladie_congenitale',
					safeValue(medicalInfo.maladie_congenitale)
				);
				studentData.append(
					'maladie_chronique',
					safeValue(medicalInfo.maladie_chronique)
				);
				studentData.append(
					'allergie_aliment',
					safeValue(medicalInfo.allergie_aliment)
				);
				studentData.append(
					'allergie_medicament',
					safeValue(medicalInfo.allergie_medicament)
				);
				studentData.append(
					'allergie_autre_substance',
					safeValue(medicalInfo.allergie_autre_substance)
				);
				studentData.append(
					'salledeclasse',
					safeValue(schoolInfo.salledeclasse)
				);
				studentData.append('anneescolaire', safeValue(currentSchoolYear.id));

				if (personalInfo.photo && personalInfo.photo.file) {
					studentData.append('photo', safeValue(personalInfo.photo.file));
				}
				if (
					medicalInfo.photocopie_carnet_vaccination &&
					medicalInfo.photocopie_carnet_vaccination.file
				) {
					studentData.append(
						'photocopie_carnet_vaccination',
						safeValue(medicalInfo.photocopie_carnet_vaccination.file)
					);
				}
				if (
					schoolInfo.bulletin_notes_annee_precedente &&
					schoolInfo.bulletin_notes_annee_precedente.file
				) {
					studentData.append(
						'bulletin_notes_annee_precedente',
						safeValue(schoolInfo.bulletin_notes_annee_precedente.file)
					);
				}

				// Call the EleveService to create the student
				const response = await EleveService.createEleve(studentData);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of students
				navigate('/education/eleves');
			} catch (error) {
				console.error("Erreur lors de la création de l'élève", error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Fragment>
			<Row>
				<Col lg={12} md={12} sm={12}>
					<div className="border-bottom pb-4 mb-4 d-md-flex align-items-center justify-content-between">
						<div className="mb-3 mb-md-0">
							<h1 className="mb-1 h2 fw-bold">Ajouter un nouvel élève</h1>
							<Breadcrumb>
								<Breadcrumb.Item href="/">Accueil</Breadcrumb.Item>
								<Breadcrumb.Item href="/education/eleves">
									Elèves
								</Breadcrumb.Item>
								<Breadcrumb.Item active>
									Ajouter un nouvel élève
								</Breadcrumb.Item>
							</Breadcrumb>
						</div>
						<div>
							<Link
								to="/education/eleves"
								className="btn btn-outline-secondary"
							>
								Retour à la liste des élèves
							</Link>
						</div>
					</div>
				</Col>
			</Row>

			{/*  Form  */}
			<Form
				className="mb-3"
				noValidate
				validated={validated}
				onSubmit={handleSubmit}
			>
				{showAlert && (
					<Alert variant="danger">
						<ExclamationTriangleFill size={25} className="me-1" />
						{alertMessage}
					</Alert>
				)}
				<GKStepper currentStep={currentStep} steps={steps} />
			</Form>
		</Fragment>
	);
};

export default AddNewStudent;
